import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "@fontsource-variable/inter";
import "./index.css";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { ThemeProvider } from "./contexts/ThemeContext";
import { installZodSpanishErrorMap } from "./lib/zodEsErrorMap";
import { offlineQueryPersister, shouldPersistOfflineQuery } from "./lib/queryPersistence";
import { shouldRetryQuery } from "./lib/queryRetry";
import { PwaUpdatePrompt } from "./components/pwa/PwaUpdatePrompt";
import { PwaInstallPrompt } from "./features/pwa/components/PwaInstallPrompt";
import { startPwaUpdateWatch } from "./features/pwa/services/pwaUpdate.service";

// Mensajes de validación de formularios SIEMPRE en español (red de seguridad
// para esquemas Zod sin mensaje custom).
installZodSpanishErrorMap();

// Busca versiones nuevas mientras la pestaña sigue abierta. El registro del
// service worker lo hace `PwaUpdatePrompt` (virtual:pwa-register/react); esto
// sólo se asegura de que una PWA instalada y abierta días se entere.
startPwaUpdateWatch();

// Crear una instancia de QueryClient para toda la aplicación
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutos
      gcTime: 1000 * 60 * 30, // 30 min en caché tras quedar sin uso
      // Evita re-lanzar TODAS las queries (incluidas las pesadas de lecturas,
      // hasta 10k filas × N sensores) cada vez que la pestaña recupera foco.
      // Los datos siguen refrescándose por `staleTime`, montaje y realtime.
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      networkMode: "offlineFirst",
      // Un reintento solo si el fallo es transitorio (red, 5xx). Permisos,
      // sesión caducada o "no existe" no cambian al repetir: mostrar el error ya.
      retry: shouldRetryQuery,
    },
  },
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: offlineQueryPersister,
        maxAge: 1000 * 60 * 60 * 24,
        buster: "agricos-pwa-v1",
        dehydrateOptions: {
          shouldDehydrateQuery: (query) => {
            return (
              query.state.status === "success" &&
              query.state.data != null &&
              shouldPersistOfflineQuery(query.queryKey)
            );
          },
        },
      }}
    >
      <ThemeProvider>
        <App />
        <PwaUpdatePrompt />
        <PwaInstallPrompt />
      </ThemeProvider>
    </PersistQueryClientProvider>
  </StrictMode>
);
